/**
 * Onboarding Orchestrator — choreographs the "Watch This" 3-beat sequence.
 *
 * Beat 1: CEO walks to user, greets them, [Show Me] button
 * Beat 2: Research demo — Sam writes on whiteboard, task queue, filler chat, results
 * Beat 3: Phone call finale — introduce EA, CEO closes BEFORE call fires
 */

import * as THREE from 'three';
import { OnboardingState } from './onboarding-state';
import { OnboardingEffects } from './onboarding-effects';
import { OnboardingWhiteboard } from './onboarding-whiteboard';
import { TaskQueue } from '@/ui/task-queue';
import { DeliverableModal } from '@/ui/deliverable-modal';
import type { WorkerManager } from '@/scenes/interior/worker-manager';
import type { ChatPanel } from '@/ui/chat-panel';
import type { HUD } from '@/ui/hud';
import type { StockAnalysisData } from '@/types';
import {
  MGR_FRONT_CENTER, MGR_FACE_CAMERA_ROT,
  MGR_TOWARD_RESEARCH, MGR_TOWARD_EA,
  DESK_POSITIONS, BEAT_PAUSE_DURATION,
  WORKER_DESK_OFFSET_Z,
} from '@/utils/constants';

// Sam's whiteboard position (whiteboard offset + worker 1 spread)
const SAM_WHITEBOARD_POS = new THREE.Vector3(-3.5, 0, 0.6);
const SAM_WHITEBOARD_ROT = -Math.PI / 2;

export class OnboardingOrchestrator {
  private state: OnboardingState;
  private effects: OnboardingEffects;
  private whiteboard: OnboardingWhiteboard;
  private taskQueue: TaskQueue;
  private deliverableModal: DeliverableModal;
  private workerManager: WorkerManager;
  private chatPanel: ChatPanel;
  private hud: HUD;
  private scene: THREE.Scene;
  private started = false;

  constructor(
    workerManager: WorkerManager,
    chatPanel: ChatPanel,
    hud: HUD,
    scene: THREE.Scene,
  ) {
    this.workerManager = workerManager;
    this.chatPanel = chatPanel;
    this.hud = hud;
    this.scene = scene;
    this.state = new OnboardingState();
    this.effects = new OnboardingEffects(scene);
    this.effects.setWorkerManager(workerManager);
    this.whiteboard = new OnboardingWhiteboard(scene);
    this.deliverableModal = new DeliverableModal();
    this.taskQueue = new TaskQueue();
    this.taskQueue.setModal(this.deliverableModal);

    // Listen for "View Findings" clicks from the task queue
    window.addEventListener('view-stock-findings', ((e: CustomEvent) => {
      this.deliverableModal.showStockAnalysis(e.detail);
    }) as EventListener);
  }

  async start(): Promise<boolean> {
    if (this.state.isComplete || this.started) return false;
    this.started = true;

    this.workerManager.addAllWorkers();
    this.hud.hide();

    this.chatPanel.setOnboardingMode(true);
    this.chatPanel.setHeaderOverride('Alex', 'Manager');
    this.chatPanel.show();
    this.chatPanel.open();

    await this.delay(2000);
    this.runBeat1();
    return true;
  }

  // ── Beat 1: CEO Approaches the User ─────────────────────────

  private runBeat1(): void {
    this.state.transition('BEAT_1_CEO_APPROACH');

    this.workerManager.walkWorkerTo(
      0,
      MGR_FRONT_CENTER,
      MGR_FACE_CAMERA_ROT,
      'IDLE',
      () => {
        this.state.transition('BEAT_1_WAITING');

        this.chatPanel.addOnboardingMessage(
          "Nice to meet you! I'm Alex, your Hatchlings manager. Before you put us to work — want to see what your team can actually do? No setup needed. Just watch.",
          'Alex',
          () => {
            this.chatPanel.addInlineButton('Show Me', () => {
              this.runBeat2();
            });
          },
        );
      },
    );
  }

  // ── Beat 2: Research Demo with Whiteboard + Task Queue ──────
  //
  // Slowed-down, theatrical pacing:
  //   1. CEO assigns task → PAUSE
  //   2. Sam walks to whiteboard → writes → PAUSE → task queue appears
  //   3. Sam walks back to desk → starts working
  //   4. CEO: "Sam's on it, as you can see on the board and task center"
  //   5. PAUSE — wait for research API (already running since step 1)
  //   6. Sam walks to whiteboard → crosses off → walks back → ✅
  //   7. CEO: "Let's see what he found" → results card → [Continue]

  private stockPromise: Promise<StockAnalysisData> | null = null;

  private runBeat2(): void {
    this.state.transition('BEAT_2_RESEARCH_INTRO');

    const researchWorkerPos = DESK_POSITIONS[1].position.clone();
    researchWorkerPos.z += WORKER_DESK_OFFSET_Z;
    const rotToResearch = this.calcRotationToward(MGR_FRONT_CENTER, researchWorkerPos);

    // Fire stock API AS SOON as CEO starts walking — maximize head start
    this.stockPromise = this.fetchStockAnalysis();

    // CEO turns toward Research desk
    this.workerManager.walkWorkerTo(0, MGR_TOWARD_RESEARCH, rotToResearch, 'IDLE', () => {
      // ── Step 1: CEO assigns the task ──
      this.chatPanel.addOnboardingMessage(
        "Sam — pull up Apple's stock performance this week. Chart it.",
        'Alex',
        () => {
          // Show search icon on Sam
          this.effects.showStatusIcon(1, 'search');
          this.state.transition('BEAT_2_RESEARCH_WORKING');

          // ── PAUSE after CEO assigns — let it breathe ──
          this.delay(1500).then(() => this.beat2_samWalksToBoard());
        },
      );
    });
  }

  /** Step 2: Sam walks to whiteboard, writes the task */
  private beat2_samWalksToBoard(): void {
    this.workerManager.walkWorkerTo(1, SAM_WHITEBOARD_POS, SAM_WHITEBOARD_ROT, 'WRITING', () => {
      // Write task on the whiteboard
      this.whiteboard.writeTask('AAPL stock analysis + chart');

      // ── PAUSE — let user see the whiteboard text ──
      this.delay(1200).then(() => {
        // Task queue slides in
        this.taskQueue.addTask('stock', 'AAPL stock analysis + chart', 'Sam');
        this.taskQueue.show();

        // ── PAUSE — let user see the task queue ──
        this.delay(1500).then(() => this.beat2_samWalksBackToDesk());
      });
    });
  }

  /** Step 3: Sam walks back to desk, CEO explains what's happening */
  private beat2_samWalksBackToDesk(): void {
    // Sam goes back to desk and starts thinking/working
    this.workerManager.commandWorker(1, 'think');

    // CEO walks back to face the user
    this.workerManager.walkWorkerTo(0, MGR_FRONT_CENTER, MGR_FACE_CAMERA_ROT, 'IDLE', () => {
      // ── Step 4: CEO fills time while stock analysis runs ──
      this.chatPanel.addOnboardingMessage(
        "Sam's on it — as you can see on the board and task center. This isn't a chatbot. Your team does real work. Let's see what he pulls up.",
        'Alex',
        () => {
          // ── PAUSE — builds anticipation + masks API latency ──
          this.delay(2000).then(() => this.beat2_waitForResults());
        },
      );
    });
  }

  /** Step 5–6: Wait for API, then Sam crosses off the task */
  private beat2_waitForResults(): void {
    if (!this.stockPromise) return;

    this.stockPromise.then((stockData) => {
      // Stock data is back! Mark task complete in queue + swap icon
      this.taskQueue.updateTask('stock', 'complete');
      this.effects.hideStatusIcon(1);
      this.effects.showStatusIcon(1, 'check');

      // ── PAUSE — let user see the ✅ in task queue ──
      this.delay(1000).then(() => {
        // Sam walks to whiteboard to cross off the task
        this.workerManager.walkWorkerTo(1, SAM_WHITEBOARD_POS, SAM_WHITEBOARD_ROT, 'WRITING', () => {
          this.whiteboard.crossOffTask();

          // ── PAUSE — let user see the strikethrough ──
          this.delay(1200).then(() => {
            // Sam walks back to desk
            this.workerManager.commandWorker(1, 'type');

            // ── PAUSE — Sam settles, then CEO presents results ──
            this.delay(800).then(() => {
              this.beat2_showResults(stockData);
            });
          });
        });
      });
    });
  }

  /** Step 7: CEO presents stock chart + [Continue] button */
  private beat2_showResults(stockData: StockAnalysisData): void {
    this.state.transition('BEAT_2_RESEARCH_RESULTS');
    this.effects.hideStatusIcon(1);

    // Add "View Findings" to task queue card
    this.taskQueue.addViewFindings('stock', stockData);

    this.chatPanel.addOnboardingMessage(
      "Done. Here's Apple's stock this week — live data, charted.",
      'Alex',
      () => {
        // Show unified stock card with expand capability
        this.chatPanel.addStockChartCard(stockData, () => {
          this.deliverableModal.showStockAnalysis(stockData);
        });

        // ── [Continue] button — let the human read at their own pace ──
        this.delay(1500).then(() => {
          this.chatPanel.addInlineButton('Continue', () => {
            this.delay(BEAT_PAUSE_DURATION).then(() => this.runBeat3());
          });
        });
      },
    );
  }

  private async fetchStockAnalysis(): Promise<StockAnalysisData> {
    try {
      const response = await fetch('/api/onboarding/stock-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      return data as StockAnalysisData;
    } catch (err) {
      console.error('Stock analysis API error:', err);
      // Fallback mock data
      return {
        ticker: 'AAPL',
        companyName: 'Apple Inc.',
        currentPrice: '$232.47',
        change: '+3.12',
        changePercent: '+1.36%',
        marketCap: '$3.54T',
        summary: 'Apple shares rose this week on strong iPhone demand and AI strategy optimism.',
        pricePoints: [
          { label: 'Mon', price: 228.50 },
          { label: 'Tue', price: 226.80 },
          { label: 'Wed', price: 229.35 },
          { label: 'Thu', price: 231.10 },
          { label: 'Fri', price: 232.47 },
        ],
      };
    }
  }

  // ── Beat 3: Phone Call Finale ─────────────────────────────────

  private runBeat3(): void {
    this.state.transition('BEAT_3_PHONE_INTRO');

    const eaWorkerPos = DESK_POSITIONS[2].position.clone();
    eaWorkerPos.z += WORKER_DESK_OFFSET_Z;
    const rotToEA = this.calcRotationToward(MGR_FRONT_CENTER, eaWorkerPos);

    // CEO turns toward EA desk
    this.workerManager.walkWorkerTo(0, MGR_TOWARD_EA, rotToEA, 'IDLE', () => {
      this.chatPanel.addOnboardingMessage(
        'This is Jordan, your executive assistant. Calls, calendar, emails — Jordan handles all of it.',
        'Alex',
        () => {
          this.delay(1200).then(() => {
            // CEO turns back to user
            this.workerManager.walkWorkerTo(0, MGR_FRONT_CENTER, MGR_FACE_CAMERA_ROT, 'IDLE', () => {
              this.state.transition('BEAT_3_PHONE_MODAL');

              // Phone icon on Jordan
              this.effects.showStatusIcon(2, 'phone');

              this.chatPanel.addOnboardingMessage(
                'Jordan, say hello to the boss. Drop your number below.',
                'Alex',
                () => {
                  // Inline phone input
                  this.chatPanel.addPhoneInput(
                    (phoneNumber: string) => {
                      this.doPhoneCall(phoneNumber);
                    },
                    () => {
                      // Skip path
                      this.effects.hideStatusIcon(2);
                      this.state.transition('BEAT_3_PHONE_DONE');

                      this.chatPanel.addOnboardingMessage(
                        "No worries — you can try that anytime. So, what would you like us to work on?",
                        'Alex',
                        () => {
                          this.finishOnboarding();
                        },
                      );
                    },
                  );
                },
              );
            });
          });
        },
      );
    });
  }

  private doPhoneCall(phoneNumber: string): void {
    this.state.transition('BEAT_3_PHONE_CALLING');

    // CEO delivers closing message FIRST — wait for typewriter to FINISH before calling
    this.chatPanel.addOnboardingMessage(
      "Once you've chatted, just let me know what you'd like us to start working on.",
      'Alex',
      () => {
        // CEO is done talking. NOW Jordan calls.
        this.chatPanel.addOnboardingMessage(
          'Calling you now...',
          'Jordan',
        );

        // Fire the actual phone call
        fetch('/api/onboarding/phone-call', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phoneNumber }),
        }).catch(err => console.error('Phone call error:', err));

        // Wait for call to complete (~20 seconds), then silently wrap up
        this.delay(20000).then(() => {
          this.effects.hideStatusIcon(2);
          this.effects.showStatusIcon(2, 'check');
          this.state.transition('BEAT_3_PHONE_DONE');

          this.delay(2000).then(() => {
            this.effects.hideStatusIcon(2);
            this.finishOnboarding();
          });
        });
      },
    );
  }

  // ── Finish: Unlock chat, show HUD ────────────────────────────

  private finishOnboarding(): void {
    this.state.transition('BEAT_5_HANDOFF');

    this.chatPanel.setOnboardingMode(false);
    this.chatPanel.clearHeaderOverride();
    this.chatPanel.pulseInput();

    // Dispose onboarding task queue so it doesn't stack under TaskProgress
    this.taskQueue.hide();
    this.taskQueue.dispose();

    // Clear whiteboard
    this.whiteboard.clear();

    this.hud.show();
    this.hud.setWorkerCount(this.workerManager.getWorkerCount());

    // CEO walks back to desk
    this.workerManager.commandWorker(0, 'type');

    this.state.transition('COMPLETE');

    // Notify main.ts so it can start ambient engine, etc.
    window.dispatchEvent(new CustomEvent('onboarding-complete'));
  }

  // ── Helpers ─────────────────────────────────────────────────

  private calcRotationToward(from: THREE.Vector3, to: THREE.Vector3): number {
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    return Math.atan2(dx, dz);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  update(delta: number): void {
    this.effects.update(delta);
  }

  get isActive(): boolean {
    return this.state.isActive;
  }

  get isComplete(): boolean {
    return this.state.isComplete;
  }

  reset(): void {
    this.state.reset();
    this.started = false;
  }
}
